import { GetSurveyMetadataRequest } from "../Models/Surveys/SurveyDataModel";

export interface RequestDataSupplierInfo {
    existingSupplierIdsToInventoryIds: { [supplierId: string]: string[] }
    newSupplierNamesToEmail: {[supplierName: string]: string}
};

// ===============================================================================

/**
 * A manager class to handle the getting and setting survey data in local
 * storage. This is primary used to pass data from the .net side of the app
 * to the react side.
 */
export class SurveyLocalStorageManager {
    // Local storage key prefix to get the supplier ids and inventories id from storage
    private static readonly _supplierIdsAndInventoriesIdKeyPrefix = "supplier-ids-inventory-ids-key-";

    // Local storage key to get the supplier ids and inventories id from storage. Default is null
    // until the user id is added to it in the constuctor.
    private _supplierIdsAndInventoriesIdKey: string;

    // ===============================================================================

    constructor(userId: string) {
        this._supplierIdsAndInventoriesIdKey = SurveyLocalStorageManager._supplierIdsAndInventoriesIdKeyPrefix + userId
    }

    // ===============================================================================

    /**
     * Sets the new survey data as a JSON string in local storage. Then
     * this methods navigates to the specified path.
     * 
     * @param data the new survey data to be stored in local storage.
     * @param path the path to navigate to after the local storage is set.
     */
    public setNewSurveyDataAndNavigate(data: RequestDataSupplierInfo, path: string) {
        this.setRequestDataSupplierInfo(data);
        window.location.href = path;
    }

    // ===============================================================================

    /**
     * Gets supplier and material data from local storage for the auto-fill feature
     * of the request data page. This method automatically clears the local storage after
     */
    public getAndClearRequestDataSupplierInfo(): RequestDataSupplierInfo | null {
        const dataJson = localStorage.getItem(this._supplierIdsAndInventoriesIdKey);

        // Item did not exist
        if (dataJson === null) {
            return null;
        }

        let data: RequestDataSupplierInfo | null = null;

        try {
            data = JSON.parse(dataJson) as RequestDataSupplierInfo;
        }
        catch {
            console.error(`ERROR in getAndClearSupplierIdsAndInventoryIds. Failed to parse JSON: ${dataJson}`);
        }

        localStorage.removeItem(this._supplierIdsAndInventoriesIdKey);

        return data;
    }

    // ===============================================================================

    /**
     * Gets supplier and material data from local storage for the auto-fill feature
     * of the request data page.
     */
    public getRequestDataSupplierInfo(): RequestDataSupplierInfo | null {
        const dataJson = localStorage.getItem(this._supplierIdsAndInventoriesIdKey);
        // Item did not exist
        if (dataJson === null) {
            return null;
        }

        let data: RequestDataSupplierInfo | null = null;

        try {
            data = JSON.parse(dataJson) as RequestDataSupplierInfo;
        }
        catch {
            console.error(`ERROR in getAndClearSupplierIdsAndInventoryIds. Failed to parse JSON: ${dataJson}`);
        }

        return data;
    }

    // ===============================================================================

    /**
     * Sets the data needed for the request data page in local storage.
     * 
     * @param requestDataSupplierInfo the data to store
     */
    public setRequestDataSupplierInfo(requestDataSupplierInfo: RequestDataSupplierInfo) {
        try {
            const dataJson = JSON.stringify(requestDataSupplierInfo);
            localStorage.setItem(this._supplierIdsAndInventoriesIdKey, dataJson);
        }
        catch {
            console.error(`ERROR in setSupplierIdsAndInventoryIds. Failed to stringify data: ${requestDataSupplierInfo}`);
        }
    }
}